import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    TierFeatureKey,
    type CoachNote,
    type StudentAssessmentEvent,
    type StudentAttendance,
    type StudentProfile,
    type StudentProgress,
    type StudentSiblingSummary,
} from '@court-sync/shared';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { coachService } from '@/services/coach';
import { coachNotesService, COACH_NOTES_PAGE_SIZE } from '@/services/coachNotesService';
import type { PaginationState } from '@/components/ui/PaginatedTable';
import { studentProgressService, ASSESSMENT_EVENTS_PAGE_SIZE } from '@/services/studentProgressService';
import { programService } from '@/services/program';
import { useCoachAcademyFeatureAccess } from '@/hooks/useFeatureAccess';
import { academyStudentService } from '@/services/academyStudentService';
import { studentService } from '@/services/student';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import {
    User,
    Calendar,
    Phone,
    Heart,
    TrendingUp,
    MessageSquare,
    GitBranch,
    BookOpen,
    Mail,
    Users,
    Loader2,
    Activity,
} from 'lucide-react';
import { CoachNotesPanel, type CoachPathwaySkillOption, type CoachNoteCreatePayload } from '@/components/students/CoachNotesPanel';
import { ProgressChart } from '@/components/students/ProgressChart';
import { CoachSportProgressForm } from '@/components/students/CoachSportProgressForm';
import { useAcademySports } from '@/hooks/useAcademySports';
import { StudentProgramSkillsPanel } from '@/components/students/StudentProgramSkillsPanel';
import { StudentLessonsPanel } from '@/components/students/StudentLessonsPanel';
import { AssessmentHistoryTimeline } from '@/components/students/AssessmentHistoryTimeline';
import { AttendanceSummary } from '@/components/students';
import { useFormatDate } from '@/hooks/useFormatDate';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { UserAvatar } from '@/components/UserAvatar';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';

type TabType =
    | 'profile'
    | 'attendance'
    | 'assessmentSnapshot'
    | 'pathway'
    | 'lessons'
    | 'progressHistory'
    | 'notes';

const calculateAge = (dateOfBirth: Date | undefined | null): number | null => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const getSkillLevelVariant = (level: string): 'default' | 'secondary' | 'success' | 'warning' => {
    switch (level) {
        case 'advanced':
            return 'success';
        case 'intermediate':
            return 'warning';
        default:
            return 'secondary';
    }
};

export default function CoachStudentDetailPage() {
    const { studentId } = useParams<{ studentId: string }>();
    const navigate = useNavigate();
    const { academyId, academy, academySlug } = useCoachAcademyStore();
    const { formatDate } = useFormatDate();

    const { hasAccess: hasProgramsAccess } = useCoachAcademyFeatureAccess(
        academy,
        academySlug,
        TierFeatureKey.PROGRAMS
    );
    const { sports: academySports, primarySport } = useAcademySports(academy);

    const [activeTab, setActiveTab] = useState<TabType>('profile');
    const [student, setStudent] = useState<StudentProfile | null>(null);
    const [progress, setProgress] = useState<StudentProgress | null>(null);
    const [attendance, setAttendance] = useState<StudentAttendance[]>([]);
    const [notes, setNotes] = useState<CoachNote[]>([]);
    const [notesPage, setNotesPage] = useState(1);
    const [notesPagination, setNotesPagination] = useState<PaginationState | null>(null);
    const [notesLoading, setNotesLoading] = useState(false);
    const [notesRefreshNonce, setNotesRefreshNonce] = useState(0);
    const [programIdsForStudent, setProgramIdsForStudent] = useState<string[]>([]);
    const [pathwaySkillOptions, setPathwaySkillOptions] = useState<CoachPathwaySkillOption[]>([]);
    const [pathwayOptionsLoading, setPathwayOptionsLoading] = useState(false);
    const [programNameById, setProgramNameById] = useState<Record<string, string>>({});
    const [pathwayRefreshNonce, setPathwayRefreshNonce] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [primaryLessonLabel, setPrimaryLessonLabel] = useState<string | null>(null);
    const [siblings, setSiblings] = useState<StudentSiblingSummary[]>([]);
    const [siblingsLoading, setSiblingsLoading] = useState(false);
    const [assessmentEvents, setAssessmentEvents] = useState<StudentAssessmentEvent[]>([]);
    const [assessmentEventsPage, setAssessmentEventsPage] = useState(1);
    const [assessmentEventsPagination, setAssessmentEventsPagination] = useState<PaginationState | null>(null);
    const [assessmentEventsLoading, setAssessmentEventsLoading] = useState(false);
    const [assessmentEventsRefreshNonce, setAssessmentEventsRefreshNonce] = useState(0);
    const [lessonNameById, setLessonNameById] = useState<Record<string, string>>({});

    const loadStudentData = useCallback(
        async (id: string) => {
            if (!academyId) return;
            setLoading(true);
            setError(null);
            try {
                const [studentResponse, progressResponse, attendanceResponse, lessonsRes, programIds] =
                    await Promise.all([
                        academyStudentService.getStudentProfile(academyId, id),
                        studentProgressService.getStudentProgress(id, academyId),
                        studentService.getStudentAttendance(id, academyId),
                        academyStudentService.getStudentLessons(academyId, id).catch(() => ({ current: [], history: [] })),
                        hasProgramsAccess
                            ? coachService.getStudentProgramIds(academyId, id).catch(() => [] as string[])
                            : Promise.resolve([] as string[]),
                    ]);

                if (!studentResponse.data) {
                    setError('Student not found');
                    setStudent(null);
                    setLessonNameById({});
                    return;
                }

                setStudent(studentResponse.data);
                setProgress(progressResponse.data ?? null);
                setAttendance(attendanceResponse.data || []);
                setProgramIdsForStudent(programIds);
                const lessonNames: Record<string, string> = {};
                for (const item of [...(lessonsRes.current ?? []), ...(lessonsRes.history ?? [])]) {
                    lessonNames[item.lessonId] = item.lessonName;
                }
                setLessonNameById(lessonNames);
                const firstCurrent = lessonsRes.current?.[0];
                setPrimaryLessonLabel(firstCurrent?.lessonName ?? null);
            } catch (err: unknown) {
                console.error('Error loading student data:', err);
                const ax = err as { response?: { data?: { error?: string } }; message?: string };
                const message = ax.response?.data?.error || ax.message || 'Failed to load student data';
                setError(message);
                setStudent(null);
                setLessonNameById({});
            } finally {
                setLoading(false);
            }
        },
        [academyId, hasProgramsAccess]
    );

    useEffect(() => {
        if (studentId && academyId) {
            void loadStudentData(studentId);
        }
    }, [studentId, academyId, loadStudentData]);

    useLayoutEffect(() => {
        setNotesPage(1);
        setAssessmentEventsPage(1);
    }, [studentId]);

    const fetchNotesPage = useCallback(
        async (page: number) => {
            if (!academyId || !studentId) return;
            setNotesLoading(true);
            try {
                const res = await coachNotesService.getStudentNotes(studentId, academyId, true, {
                    page,
                    limit: COACH_NOTES_PAGE_SIZE,
                });
                setNotes(res.data || []);
                setNotesPagination(res.pagination ?? null);
            } catch (error) {
                console.error('Failed to load coach notes:', error);
            } finally {
                setNotesLoading(false);
            }
        },
        [academyId, studentId]
    );

    useEffect(() => {
        if (!academyId || !studentId) return;
        void fetchNotesPage(notesPage);
    }, [academyId, studentId, notesPage, notesRefreshNonce, fetchNotesPage]);

    const fetchAssessmentEventsPage = useCallback(
        async (page: number) => {
            if (!academyId || !studentId) return;
            setAssessmentEventsLoading(true);
            try {
                const res = await studentProgressService.getAssessmentEvents(studentId, academyId, {
                    page,
                    limit: ASSESSMENT_EVENTS_PAGE_SIZE,
                });
                setAssessmentEvents(res.data || []);
                setAssessmentEventsPagination(res.pagination ?? null);
            } catch (error) {
                console.error('Failed to load assessment events:', error);
                setAssessmentEvents([]);
                setAssessmentEventsPagination({
                    page: 1,
                    limit: ASSESSMENT_EVENTS_PAGE_SIZE,
                    total: 0,
                    totalPages: 0,
                });
            } finally {
                setAssessmentEventsLoading(false);
            }
        },
        [academyId, studentId]
    );

    useEffect(() => {
        if (!academyId || !studentId) return;
        void fetchAssessmentEventsPage(assessmentEventsPage);
    }, [academyId, studentId, assessmentEventsPage, assessmentEventsRefreshNonce, fetchAssessmentEventsPage]);

    useEffect(() => {
        if (!academyId || !studentId) {
            return () => {};
        }
        let cancelled = false;
        setSiblingsLoading(true);
        void academyStudentService
            .getStudentSiblings(academyId, studentId)
            .then((list) => {
                if (!cancelled) setSiblings(list);
            })
            .catch(() => {
                if (!cancelled) setSiblings([]);
            })
            .finally(() => {
                if (!cancelled) setSiblingsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [academyId, studentId]);

    const showProgramSkills =
        hasProgramsAccess && programIdsForStudent.length > 0 && !!studentId && !!academyId;

    useEffect(() => {
        if (!studentId || !academyId || !hasProgramsAccess || programIdsForStudent.length === 0) {
            setPathwaySkillOptions([]);
            setProgramNameById({});
            setPathwayOptionsLoading(false);
            return undefined;
        }
        let cancelled = false;
        setPathwayOptionsLoading(true);
        void (async () => {
            try {
                const allPrograms = await programService.getPrograms(academyId, true);
                const relevant = allPrograms.filter((p) => programIdsForStudent.includes(p.id));
                const names: Record<string, string> = {};
                const opts: CoachPathwaySkillOption[] = [];
                for (const p of relevant) {
                    names[p.id] = p.name;
                    const rows = await programService.getStudentProgramSkills(p.id, studentId, academyId);
                    const byName = new Map(rows.map((r) => [r.skillName, r]));
                    for (const t of p.targetSkills) {
                        opts.push({
                            programId: p.id,
                            programName: p.name,
                            skillName: t.name,
                            completed: byName.get(t.name)?.completed ?? false,
                        });
                    }
                }
                if (!cancelled) {
                    setPathwaySkillOptions(opts);
                    setProgramNameById(names);
                }
            } catch (e) {
                console.error('Failed to load pathway options:', e);
                if (!cancelled) {
                    setPathwaySkillOptions([]);
                }
            } finally {
                if (!cancelled) {
                    setPathwayOptionsLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [studentId, academyId, hasProgramsAccess, programIdsForStudent, pathwayRefreshNonce]);

    const tabs = useMemo(
        () =>
            [
                { id: 'profile' as const, label: 'Profile', icon: <User className="w-4 h-4" /> },
                { id: 'lessons' as const, label: 'Lessons', icon: <BookOpen className="w-4 h-4" /> },
                { id: 'attendance' as const, label: 'Attendance', icon: <Calendar className="w-4 h-4" /> },
                { id: 'notes' as const, label: 'Notes', icon: <MessageSquare className="w-4 h-4" /> },
                {
                    id: 'assessmentSnapshot' as const,
                    label: 'Assessment Snapshot',
                    mobileLabel: 'Snapshot',
                    icon: <Activity className="w-4 h-4" />,
                },
                ...(showProgramSkills
                    ? [{ id: 'pathway' as const, label: 'Pathway', icon: <GitBranch className="w-4 h-4" /> }]
                    : []),
                {
                    id: 'progressHistory' as const,
                    label: 'Progress History',
                    mobileLabel: 'History',
                    icon: <TrendingUp className="w-4 h-4" />,
                },
            ] as const,
        [showProgramSkills]
    );

    useEffect(() => {
        if (!showProgramSkills && activeTab === 'pathway') {
            setActiveTab('profile');
        }
    }, [showProgramSkills, activeTab]);

    const refreshSportProgressAndEvents = async () => {
        if (!studentId || !academyId) return;
        setAssessmentEventsPage(1);
        setAssessmentEventsRefreshNonce((n) => n + 1);
        const progressRes = await studentProgressService.getStudentProgress(studentId, academyId);
        setProgress(progressRes.data ?? null);
    };

    const refreshAfterPathwayAssessment = async () => {
        if (!studentId || !academyId) return;
        setNotesPage(1);
        setNotesRefreshNonce((n) => n + 1);
        setPathwayRefreshNonce((n) => n + 1);
        setAssessmentEventsPage(1);
        setAssessmentEventsRefreshNonce((n) => n + 1);
        const progressRes = await studentProgressService.getStudentProgress(studentId, academyId);
        setProgress(progressRes.data ?? null);
    };

    const handleAddNote = async (noteData: CoachNoteCreatePayload) => {
        if (!studentId || !academyId) return;
        try {
            await coachNotesService.createNote(studentId, {
                academyId,
                content: noteData.content,
                category: noteData.category,
                isPrivate: noteData.isPrivate,
                ...(noteData.programId && noteData.programSkillName != null && noteData.rating != null
                    ? {
                          programId: noteData.programId,
                          programSkillName: noteData.programSkillName,
                          rating: noteData.rating,
                          skillAchieved: noteData.skillAchieved,
                      }
                    : {}),
            });
            await refreshAfterPathwayAssessment();
        } catch (error) {
            console.error('Error adding note:', error);
        }
    };

    const pathwayEmptyHint =
        !hasProgramsAccess
            ? 'Pathway skill ratings require the Programs feature (Professional or higher).'
            : programIdsForStudent.length === 0
              ? 'This student has no program lesson bookings yet. Pathway skills appear after they are enrolled in a lesson linked to a program.'
              : !pathwayOptionsLoading && pathwaySkillOptions.length === 0
                ? 'No target skills are configured for this student’s programs. Add target skills in Program management.'
                : null;

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                <p className="ml-2 text-gray-500 dark:text-gray-400">Loading student...</p>
            </div>
        );
    }

    if (error || !student) {
        return (
            <div className="min-h-screen">
                <main className="contents">
                    <div className="flex flex-col items-center justify-center py-20">
                        <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Student not found'}</p>
                    </div>
                </main>
            </div>
        );
    }

    const age = calculateAge(student.dateOfBirth);

    return (
        <div className="min-h-screen">
            <main className="contents">
                <PageHeader
                    title={`${student.firstName} ${student.lastName}`}
                    breadcrumbs={[
                        { label: 'Students', href: `/academy/${academySlug}/coach/students` },
                    ]}
                    subtitle={`Age ${age != null ? age : '—'} · ${student.gender ? student.gender.charAt(0).toUpperCase() + student.gender.slice(1) : '—'} · Joined ${formatDate(student.createdAt)}`}
                    help={
                        <div className="space-y-2">
                            <p>
                                Age {age != null ? age : '—'} · <span className="capitalize">{student.gender}</span> ·
                                Joined {formatDate(student.createdAt)}
                            </p>
                            {(primaryLessonLabel || !student.isActive) && (
                                <div className="flex flex-wrap items-center gap-2">
                                    {primaryLessonLabel ? (
                                        <Badge variant="secondary">{primaryLessonLabel}</Badge>
                                    ) : null}
                                    {!student.isActive ? <Badge variant="outline">Inactive</Badge> : null}
                                </div>
                            )}
                        </div>
                    }
                />

                {student.progressSummary?.recentAchievements.length ? (
                    <div className="mb-6 flex flex-wrap items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            <span>{student.progressSummary.recentAchievements.length} achievement(s)</span>
                        </div>
                    </div>
                ) : null}

                <DashboardTabNav<TabType>
                    tabs={tabs}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                    accent="primary"
                    ariaLabel="Student sections"
                />

                <div className="space-y-6">
                    {activeTab === 'profile' && (
                        <div className="grid gap-6 md:grid-cols-2">
                            <Card>
                                <CardHeader>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <User className="w-5 h-5" />
                                        Basic Information
                                    </h3>
                                </CardHeader>
                                <CardContent>
                                    <dl className="space-y-4">
                                        <div>
                                            <dt className="text-sm text-gray-500 dark:text-gray-400">Full Name</dt>
                                            <dd className="text-gray-900 dark:text-white">
                                                {student.firstName} {student.lastName}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-sm text-gray-500 dark:text-gray-400">Date of Birth</dt>
                                            <dd className="text-gray-900 dark:text-white">
                                                {student.dateOfBirth ? (
                                                    <>
                                                        {formatDate(student.dateOfBirth)} (
                                                        {age != null ? `${age} years old` : 'Age unknown'})
                                                    </>
                                                ) : (
                                                    <>Not set</>
                                                )}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-sm text-gray-500 dark:text-gray-400">Gender</dt>
                                            <dd className="text-gray-900 dark:text-white capitalize">{student.gender}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-sm text-gray-500 dark:text-gray-400">Skill Level</dt>
                                            <dd>
                                                <Badge variant={getSkillLevelVariant(student.skillLevel)}>
                                                    {student.skillLevel}
                                                </Badge>
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-sm text-gray-500 dark:text-gray-400">Status</dt>
                                            <dd>
                                                <Badge variant={student.isActive ? 'success' : 'secondary'}>
                                                    {student.isActive ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </dd>
                                        </div>
                                    </dl>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <Phone className="w-5 h-5" />
                                        Emergency & bookings contact
                                    </h3>
                                </CardHeader>
                                <CardContent>
                                    {student.emergencyContact ? (
                                        <dl className="space-y-4">
                                            <div>
                                                <dt className="text-sm text-gray-500 dark:text-gray-400">Name</dt>
                                                <dd className="text-gray-900 dark:text-white">
                                                    {student.emergencyContact.name}
                                                </dd>
                                            </div>
                                            <div>
                                                <dt className="text-sm text-gray-500 dark:text-gray-400">Phone</dt>
                                                <dd className="text-gray-900 dark:text-white">
                                                    <a
                                                        href={`tel:${student.emergencyContact.phone}`}
                                                        className="text-blue-600 hover:underline"
                                                    >
                                                        {student.emergencyContact.phone}
                                                    </a>
                                                </dd>
                                            </div>
                                            <div>
                                                <dt className="text-sm text-gray-500 dark:text-gray-400">Relationship</dt>
                                                <dd className="text-gray-900 dark:text-white">
                                                    {student.emergencyContact.relationship}
                                                </dd>
                                            </div>
                                            {student.emergencyContact.email ? (
                                                <div>
                                                    <dt className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                                        <Mail className="w-3.5 h-3.5" aria-hidden />
                                                        Emergency contact email
                                                    </dt>
                                                    <dd className="text-gray-900 dark:text-white">
                                                        <a
                                                            href={`mailto:${student.emergencyContact.email}`}
                                                            className="text-blue-600 hover:underline"
                                                        >
                                                            {student.emergencyContact.email}
                                                        </a>
                                                    </dd>
                                                </div>
                                            ) : null}
                                        </dl>
                                    ) : (
                                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                                            No emergency contact on file
                                        </p>
                                    )}

                                    <div className="mt-4 pt-4 border-t border-border">
                                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
                                            Online booking account
                                        </p>
                                        <dl className="space-y-3">
                                            {student.guardianEmail ? (
                                                <div>
                                                    <dt className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                                        <Mail className="w-3.5 h-3.5" aria-hidden />
                                                        Linked account email
                                                    </dt>
                                                    <dd className="text-gray-900 dark:text-white">
                                                        <a
                                                            href={`mailto:${student.guardianEmail}`}
                                                            className="text-blue-600 hover:underline"
                                                        >
                                                            {student.guardianEmail}
                                                        </a>
                                                    </dd>
                                                </div>
                                            ) : student.isManual && student.inviteEmail ? (
                                                <div>
                                                    <dt className="text-sm text-gray-500 dark:text-gray-400">
                                                        Invite email (signup pending)
                                                    </dt>
                                                    <dd className="text-gray-900 dark:text-white">
                                                        <a
                                                            href={`mailto:${student.inviteEmail}`}
                                                            className="text-blue-600 hover:underline"
                                                        >
                                                            {student.inviteEmail}
                                                        </a>
                                                        <span className="ml-1 text-xs text-muted-foreground">
                                                            (not linked yet)
                                                        </span>
                                                    </dd>
                                                </div>
                                            ) : (
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    No linked booking account yet.
                                                </p>
                                            )}
                                        </dl>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="md:col-span-2">
                                <CardHeader>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <Users className="w-5 h-5" aria-hidden />
                                        Siblings in this academy
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Other students in this academy linked to the same booking account.
                                    </p>
                                </CardHeader>
                                <CardContent>
                                    {siblingsLoading ? (
                                        <p className="text-sm text-muted-foreground">Loading…</p>
                                    ) : siblings.length === 0 ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">No siblings</p>
                                    ) : (
                                        <div className="overflow-x-auto rounded-xl border border-border bg-card text-card-foreground shadow-sm">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="border-b border-border">
                                                        <th className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Student</th>
                                                        <th className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Current lessons</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {siblings.map((sib) => (
                                                        <tr
                                                            key={sib.id}
                                                            className="border-b border-border hover:bg-muted/40 last:border-0"
                                                        >
                                                            <td className="px-4 py-3 align-middle">
                                                                <div className="flex items-center gap-2">
                                                                    <UserAvatar
                                                                        firstName={sib.firstName}
                                                                        lastName={sib.lastName}
                                                                        size="sm"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            navigate(
                                                                                `/academy/${academySlug}/coach/students/${sib.id}`
                                                                            )
                                                                        }
                                                                        className="font-medium text-primary hover:underline text-left"
                                                                    >
                                                                        {sib.firstName} {sib.lastName}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300 align-middle">
                                                                {sib.currentLessonNames.length > 0
                                                                    ? sib.currentLessonNames.join(', ')
                                                                    : '—'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="md:col-span-2">
                                <CardHeader>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <Heart className="w-5 h-5" />
                                        Medical Notes
                                    </h3>
                                </CardHeader>
                                <CardContent>
                                    {student.medicalNotes ? (
                                        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                                            <p className="text-gray-700 dark:text-gray-300">{student.medicalNotes}</p>
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                                            No medical notes on file
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {activeTab === 'attendance' && (
                        <>
                            {attendance.length > 0 ? (
                                <AttendanceSummary
                                    attendance={attendance}
                                    showDetails
                                    lessonNameById={lessonNameById}
                                    attendanceExport={
                                        studentId && academyId
                                            ? {
                                                  academyId,
                                                  studentId,
                                                  studentFirstName: student?.firstName,
                                                  studentLastName: student?.lastName,
                                              }
                                            : undefined
                                    }
                                />
                            ) : (
                                <DashboardEmptyStateCard>
                                    <DashboardEmptyStateIcon icon={Calendar} />
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        No attendance records yet
                                    </h3>
                                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                                        Attendance appears after this student is enrolled in lessons and sessions are
                                        marked.
                                    </p>
                                </DashboardEmptyStateCard>
                            )}
                        </>
                    )}

                    {activeTab === 'assessmentSnapshot' && studentId && academyId && (
                        <section aria-labelledby="coach-student-assessment-snapshot-heading">
                            <h2
                                id="coach-student-assessment-snapshot-heading"
                                className="mb-2 text-lg font-semibold text-gray-900 dark:text-white"
                            >
                                Assessment Snapshot
                            </h2>
                            <div className="mb-4 space-y-2 text-sm text-gray-600 dark:text-gray-400">
                                <p>
                                    <span className="font-medium text-gray-800 dark:text-gray-200">What this is:</span>{' '}
                                    Tennis skill bars (forehand, backhand, serve, etc.) and overall level, saved as the
                                    student&apos;s current activity record. It is{' '}
                                    <span className="font-medium">not</span> the same as program pathway ratings (those
                                    come from Coach Notes / Pathway tab and appear under{' '}
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('progressHistory')}
                                        className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                                    >
                                        Progress History
                                    </button>
                                    ).
                                </p>
                                <p>
                                    <span className="font-medium text-gray-800 dark:text-gray-200">Who can fill it:</span>{' '}
                                    You, as a coach on this tab. Academy owners can also save from the owner student
                                    profile. <span className="font-medium">Students and guardians</span> do not edit
                                    activity assessments here.
                                </p>
                                <p>
                                    <span className="font-medium text-gray-800 dark:text-gray-200">What to do:</span>{' '}
                                    Use the form below to save or update an assessment. After you save, the chart
                                    appears and an activity snapshot is added to{' '}
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('progressHistory')}
                                        className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                                    >
                                        Progress History
                                    </button>
                                    .
                                </p>
                            </div>
                            <div className="space-y-6">
                                <CoachSportProgressForm
                                    studentId={studentId}
                                    academyId={academyId}
                                    academySports={academySports}
                                    progress={progress}
                                    onSaved={refreshSportProgressAndEvents}
                                />
                                {progress ? (
                                    <ProgressChart
                                        progress={progress}
                                        sportId={primarySport?.sportId ?? 'tennis'}
                                    />
                                ) : null}
                            </div>
                        </section>
                    )}

                    {activeTab === 'pathway' && showProgramSkills && studentId && academyId && (
                        <div className="space-y-4">
                            <StudentProgramSkillsPanel
                                academyId={academyId}
                                studentId={studentId}
                                programIds={programIdsForStudent}
                                refreshKey={pathwayRefreshNonce}
                                onPathwayAssessmentRecorded={refreshAfterPathwayAssessment}
                                academyName={academy?.name ?? 'Academy'}
                                studentFirstName={student.firstName}
                                studentLastName={student.lastName}
                                academyLogoUrl={academy?.logoUrl}
                                academyPrimaryColor={academy?.primaryColor}
                            />
                        </div>
                    )}

                    {activeTab === 'lessons' && studentId && academyId && (
                        <StudentLessonsPanel academyId={academyId} studentId={studentId} />
                    )}

                    {activeTab === 'progressHistory' && studentId && (
                        <section aria-labelledby="coach-student-assessment-history-heading">
                            <h2
                                id="coach-student-assessment-history-heading"
                                className="mb-2 text-lg font-semibold text-gray-900 dark:text-white"
                            >
                                Assessment history
                            </h2>
                            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                                Chronological pathway skill ratings and sport activity snapshots from notes and progress
                                updates. To record or edit skill bars, use the{' '}
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('assessmentSnapshot')}
                                    className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                                >
                                    Assessment Snapshot
                                </button>{' '}
                                tab.
                            </p>
                            <AssessmentHistoryTimeline
                                paginated
                                events={assessmentEvents}
                                pagination={assessmentEventsPagination}
                                onPageChange={setAssessmentEventsPage}
                                loading={assessmentEventsLoading}
                                programNameById={programNameById}
                            />
                        </section>
                    )}

                    {activeTab === 'notes' && studentId && (
                        <CoachNotesPanel
                            notes={notes}
                            notesPagination={notesPagination}
                            onNotesPageChange={setNotesPage}
                            notesLoading={notesLoading}
                            isCoach={true}
                            onAddNote={handleAddNote}
                            pathwaySkillOptions={showProgramSkills ? pathwaySkillOptions : []}
                            programNameById={programNameById}
                            pathwayOptionsLoading={
                                hasProgramsAccess && showProgramSkills && pathwayOptionsLoading
                            }
                            pathwayEmptyHint={pathwayEmptyHint}
                            onSeePathwayDetails={() => setActiveTab('progressHistory')}
                        />
                    )}
                </div>
            </main>
        </div>
    );
}
